import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Clean, ShadCN-style table for Hebrew RTL content.
 *
 * Accepts a parsed table structure (headers + rows of strings/nodes) and
 * renders an editorial-quality table:
 *   - subtle stone-tinted header row
 *   - optional zebra striping
 *   - rounded outer corners with overflow clipping
 *   - generous cell padding
 *   - text-start alignment so Hebrew flows correctly RTL
 *   - horizontally scrollable on narrow viewports
 *
 * Cells accept ReactNode so callers can pass inline-formatted markdown.
 */
export type MarkdownTableProps = {
  headers: ReactNode[];
  rows: ReactNode[][];
  /** Apply alternating row tint. Default: true. */
  zebra?: boolean;
  /** Optional caption rendered below the table in muted text. */
  caption?: string;
  /** Extra classes merged onto the outer wrapper. */
  className?: string;
};

export function MarkdownTable({
  headers,
  rows,
  zebra = true,
  caption,
  className,
}: MarkdownTableProps) {
  return (
    <div
      className={cn(
        "my-4 overflow-x-auto rounded-2xl border border-stone-200/70 bg-white",
        "shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
        className,
      )}
    >
      <table className="w-full border-collapse text-[15px]">
        <thead>
          <tr className="bg-stone-50/80">
            {headers.map((h, i) => (
              <th
                key={i}
                scope="col"
                className={cn(
                  "px-5 py-3 text-start align-middle",
                  "text-[11px] font-semibold uppercase tracking-[0.14em]",
                  "text-stone-600 border-b border-stone-200/70",
                  "whitespace-nowrap",
                )}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={ri}
              className={cn(
                "transition-colors",
                zebra && ri % 2 === 1 ? "bg-stone-50/40" : "bg-white",
                ri < rows.length - 1 && "border-b border-stone-100",
                "hover:bg-amber-50/30",
              )}
            >
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={cn(
                    "px-5 py-3.5 text-start align-top",
                    "text-stone-800 leading-[1.7]",
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {caption ? (
          <caption className="caption-bottom px-5 py-3 text-start text-xs text-stone-500">
            {caption}
          </caption>
        ) : null}
      </table>
    </div>
  );
}
